import React, { useState } from 'react';

import { cn } from '@/lib/utils';
import useClickOutside from '@/hooks/useClickOutside';

export type SelectOption = {
  label: string;
  value: string;
};

type Props = {
  id: string;
  disabled?: boolean;
  onChange: (value: SelectOption) => void;
  options: SelectOption[];
  selectedValue?: SelectOption;
  label: string;
  className?: string;
  labelClassName?: string;
  /** Overrides the open dropdown box, e.g. to fit a longer list without scroll. */
  listClassName?: string;
  /** Overrides the scrollable area that holds the options. */
  optionsClassName?: string;
};

export default function Select({
  id,
  disabled = false,
  onChange,
  selectedValue,
  label,
  options,
  className,
  labelClassName,
  listClassName,
  optionsClassName,
}: Props) {
  const [open, setOpen] = useState(false);

  const ref = useClickOutside({ clickFn: () => setOpen(false) });

  return (
    <div ref={ref} className={className}>
      <div className="relative">
        <label
          className={cn(
            'text-sm text-gray-700 leading-[1.8] w-fit',
            labelClassName,
          )}
          htmlFor={id}>
          {label}
        </label>
        <button
          id={id}
          type="button"
          className={cn(
            'relative w-full min-h-[44px] h-[2.5625rem] leading-[1.625] border bg-gray-100 border-gray-200 shadow-sm pl-4 pr-10 py-2 text-left cursor-pointer focus:outline-none sm:text-sm',
            {
              'border-primary': open,
            },
          )}
          aria-haspopup="listbox"
          aria-expanded="true"
          aria-labelledby="listbox-label"
          onClick={() => setOpen(!open)}
          disabled={disabled}>
          <span className="relative text-base w-full truncate flex items-center">
            {selectedValue?.label}
          </span>
          <span
            className={`absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none ${
              disabled ? 'hidden' : ''
            }`}>
            <svg
              className="w-5 h-5 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 -960 960 960"
              fill="currentColor">
              <path d="M480-134.62 313.08-301.54l43.38-43.38L480-221.38l123.54-123.54 43.38 43.38L480-134.62Zm-123.54-478L313.08-656 480-822.92 646.92-656l-43.38 43.38L480-736.16 356.46-612.62Z" />
            </svg>
          </span>
        </button>

        <div
          className={cn(
            'max-h-0 absolute z-10 mt-1 w-full bg-white shadow-lg rounded-sm overflow-hidden text-base focus:outline-none sm:text-sm transition-[max-height] duration-[200ms] ease-in-out',
            {
              'max-h-80 border border-gray-200': open,
            },
            open && listClassName,
          )}>
          {open && (
            <div
              className={cn(
                'max-h-64 scrollbar-track-transparent scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-600 scrollbar-thumb-rounded scrollbar-thin overflow-y-scroll',
                optionsClassName,
              )}>
              {options.map(({ value, label }, index) => {
                return (
                  <li
                    key={`${id}-${index}`}
                    className={cn(
                      'text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9 flex items-center hover:bg-gray-50 transition',
                      {
                        'bg-gray-200': value === selectedValue?.value,
                      },
                    )}
                    id="listbox-option-0"
                    role="option"
                    aria-selected={value === selectedValue?.value}
                    onClick={() => {
                      onChange({ value, label });
                      setOpen(false);
                    }}>
                    <span
                      className={cn('font-normal truncate text-sm', {
                        'font-semibold': value === selectedValue?.value,
                      })}>
                      {label}
                    </span>
                  </li>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
